import React, { useState, useContext, useEffect } from 'react';
import { Select } from "antd";
import Cookies from 'js-cookie';
import axios from './axios';
import { logTypeSelectOptions } from './options';
import { getCodeListByType, getCompanyAssetTypeList } from './codeManager';
import { getFindAssetList } from './api';

const { Option } = Select;

/**
 * @description
 * 공통코드 Options
 */
function useCodeSelect(codeType, isSuperUser) {

    // 쿠키에서 SuperUser값 조회
    const companyCode = Cookies.get('auth_companyCode');
    const companyName = Cookies.get('auth_companyName');

    const codeList = localStorage.getItem('code_list');
    const [options, setOptions] = useState([{ text: '', value: '' }]);

    // superuser가 아닌 경우 cookie 회사코드 return
    if (codeType == 'com_cd' && isSuperUser != 'true') {
        return [{ text: companyName, value: companyCode }];
    } else {
        if (codeList) {
            //문자열을 DataList로 변환하여 리턴
            let filteredList = JSON.parse(codeList).filter(item => item.type == codeType);
            
            // asset_type인 경우 value 기준으로 정렬
            if (codeType === "asset_type") {
                filteredList.sort((a, b) => {
                    // value가 없는 경우 처리
                    if (!a.value) return 1;
                    if (!b.value) return -1;
                    // 알파벳순 정렬
                    return a.value.localeCompare(b.value);
                });
            }
            
            if (codeType !== "result_cd" && codeType !== "com_cd") filteredList.unshift({ type: codeType, text: "전체", value: "" });
            return filteredList;
        } else {
            return options;
        }
    }
}

/**
 * @description
 * 공통코드 SelectBox
 */
export function CodeSelect({ codeType, id, value, disabled, onChangeHandler, parentCode, multiple, showCompanyIndicator = false, selectedCompanyCode = null, useActualAssets = false }) {
    // 쿠키에서 SuperUser값 조회
    const isSuperUser = Cookies.get('auth_superuser');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [companyAssetTypes, setCompanyAssetTypes] = useState([]);

    // codeManager를 사용하여 코드 목록 조회
    useEffect(() => {
        const loadOptions = async () => {
            setLoading(true);
            try {
                // 회사코드이고 superuser가 아닌 경우
                if (codeType === 'com_cd' && isSuperUser !== 'true') {
                    const companyCode = Cookies.get('auth_companyCode');
                    const companyName = Cookies.get('auth_companyName');
                    setOptions([{ text: companyName, value: companyCode }]);
                } else if (codeType === "asset_spec" && useActualAssets && selectedCompanyCode && parentCode) {
                    // 실제 자산 데이터에서 asset_spec 조회
                    try {
                        const response = await axios.get(
                            getFindAssetList(selectedCompanyCode, parentCode, '', '', '')
                        );

                        // 자산 데이터에서 asset_spec 추출 및 중복 제거
                        let assetSpecs = [...new Set(
                            response.data
                                .map(asset => asset.asset_spec)
                                .filter(spec => spec && spec.trim() !== '')
                        )].sort();

                        // PC(Windows) 자산구분인 경우 WindowsPC를 항상 추가
                        if (parentCode === 'PC' && !assetSpecs.includes('WindowsPC')) {
                            assetSpecs.push('WindowsPC');
                            assetSpecs.sort();
                        }

                        // Select 옵션 형식으로 변환
                        const assetSpecOptions = assetSpecs.map(spec => ({
                            text: spec,
                            value: spec,
                            type: 'asset_spec'
                        }));

                        setOptions(assetSpecOptions);
                    } catch (error) {
                        console.error('Failed to load actual assets:', error);
                        // 오류 시 코드 테이블에서 가져오기
                        let codes = await getCodeListByType(codeType);
                        if (parentCode) {
                            codes = codes.filter(item => item.val_01 === parentCode);
                        }
                        setOptions(codes);
                    }
                } else {
                    // codeManager에서 코드 목록 조회
                    let codes = await getCodeListByType(codeType);

                    // asset_spec인 경우 parentCode(asset_type)로 필터링
                    if (codeType === "asset_spec" && parentCode) {
                        codes = codes.filter(item => item.val_01 === parentCode);
                    }

                    // asset_type인 경우 value 기준으로 정렬 (전체 제외)
                    if (codeType === "asset_type") {
                        // '전체' 옵션 분리
                        const allOption = codes.find(item => item.value === '');
                        const otherOptions = codes.filter(item => item.value !== '');

                        // 나머지 옵션들 정렬
                        otherOptions.sort((a, b) => {
                            return a.value.localeCompare(b.value);
                        });

                        // '전체'를 맨 앞에 넣고 정렬된 옵션들 추가
                        if (allOption) {
                            setOptions([allOption, ...otherOptions]);
                        } else {
                            setOptions(otherOptions);
                        }

                        // 회사별 자산구분 표시가 필요한 경우
                        if (showCompanyIndicator && selectedCompanyCode) {
                            try {
                                const companyAssets = await getCompanyAssetTypeList(selectedCompanyCode);
                                setCompanyAssetTypes(companyAssets);
                            } catch (error) {
                                console.error('Failed to load company asset types:', error);
                                setCompanyAssetTypes([]);
                            }
                        }
                    } else {
                        setOptions(codes);
                    }
                }
            } catch (error) {
                console.error('Failed to load options:', error);
                // 오류 시 기존 방식으로 폴백
                const fallbackOptions = useCodeSelect(codeType, isSuperUser);
                setOptions(fallbackOptions);
            }
            setLoading(false);
        };

        loadOptions();
        
        // 코드 목록 업데이트 이벤트 리스너
        const handleCodeUpdate = () => {
            loadOptions();
        };
        
        window.addEventListener('codeListUpdated', handleCodeUpdate);
        
        return () => {
            window.removeEventListener('codeListUpdated', handleCodeUpdate);
        };
    }, [codeType, isSuperUser, parentCode, showCompanyIndicator, selectedCompanyCode, useActualAssets]);

    //회사코드에 대하여 사용자유형에 따른 세팅
    if (codeType == 'com_cd' && isSuperUser == 'true') {
        disabled = false;
    }

    return (
        <Select id={id}
            size="small"
            style={{ 
                width: "80%"
            }}
            value={value}
            onChange={onChangeHandler}
            disabled={disabled}
            loading={loading}
            mode={multiple ? "multiple" : undefined}
            // 회사/자산구분 selectbox는 입력하여 검색 가능하도록 처리 (그 외 단일 선택은 antd 기본값 유지)
            showSearch={(codeType === 'com_cd' || codeType === 'asset_type') ? true : undefined}
            // 코드명/코드값 두 가지 모두로 검색되도록 직접 필터링 (Option children이 JSX라 기본 검색이 동작하지 않음)
            filterOption={(input, option) => {
                const keyword = input.trim().toLowerCase();
                if (!keyword) return true;
                const item = options.find((o) => String(o.value) === String(option?.value));
                if (!item) return false;
                return (item.text || '').toLowerCase().includes(keyword)
                    || String(item.value || '').toLowerCase().includes(keyword);
            }}
            placeholder={multiple ? "자산스펙 선택 (복수 선택 가능)" : undefined}
            maxTagCount={multiple ? 2 : undefined}
            maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}개 더`}
            tagRender={multiple ? (props) => {
                const { label, closable, onClose } = props;
                return (
                    <span
                        style={{
                            backgroundColor: '#b3d9ff',
                            color: '#333',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            marginRight: '4px',
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            border: '1px solid #87ceeb'
                        }}
                    >
                        {label}
                        {closable && (
                            <span 
                                onClick={onClose}
                                style={{
                                    marginLeft: '4px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontWeight: 'bold'
                                }}
                            >
                                ×
                            </span>
                        )}
                    </span>
                );
            } : undefined}
        >

            {options.length > 0 &&
                options.map((item, i) => {
                    // 회사별 자산구분 표시 여부 확인
                    const isCompanyAssetType = codeType === "asset_type" &&
                                             showCompanyIndicator &&
                                             item.value &&
                                             companyAssetTypes.includes(item.value);

                    return (
                        <Option key={i} value={item.value}>
                            {isCompanyAssetType ? (
                                <span style={{
                                    padding: '2px 6px',
                                    backgroundColor: '#1890ff',
                                    color: 'white',
                                    borderRadius: '3px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    display: 'inline-block'
                                }}>
                                    [{item.value}] {item.text}
                                </span>
                            ) : (
                                <span>
                                    {(item.value == '') ? '' : '[' + item.value + ']'} {item.text}
                                </span>
                            )}
                        </Option>
                    )
                })}
        </Select>
    );
}

/**
 * @description
 * 코드명 조회
 */
export function getCodeName(codeType, code) {

    let codeList;
    let codeInfo;

    if (codeType == 'logType') {
        codeList = logTypeSelectOptions;
        codeInfo = codeList.filter(item => item.value == code);
    } else {
        codeList = localStorage.getItem('code_list');
        codeInfo = JSON.parse(codeList).filter(item => item.type == codeType && item.value == code)
    }

    if (codeInfo.length > 0) {
        return codeInfo[0].text;
    } else {
        return code;
    }

}


export default CodeSelect;